import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Award, AlertTriangle, Download, ExternalLink, Loader2 } from 'lucide-react';

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/account/documents').then(r => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const certs = docs.filter(d => d.type === 'certificate');
  const appraisals = docs.filter(d => d.type === 'appraisal');
  const letters = docs.filter(d => d.type === 'counterfeit_letter');

  return (
    <div data-testid="documents-page">
      <h1 className="font-display text-2xl font-medium text-ink mb-1">My Documents</h1>
      <p className="text-sm text-muted-foreground mb-6">Download certificates, appraisal reports, and other documents.</p>

      {docs.length === 0 ? (
        <Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">No documents yet.</p></CardContent></Card>
      ) : (
        <div className="space-y-6">
          {certs.length > 0 && (
            <div>
              <h2 className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-3">Certificates of Authenticity</h2>
              <div className="space-y-2">
                {certs.map((d, i) => {
                  const pdf = d.files?.find(f => f.file_type === 'pdf');
                  const pdfUrl = pdf?.url || '';
                  return (
                    <Card key={i} className="bg-white border-stone/50">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: d.is_revoked ? 'rgba(90,31,42,0.10)' : 'rgba(14,107,93,0.10)' }}>
                            <Award className="h-4 w-4" style={{ color: d.is_revoked ? '#5A1F2A' : '#0E6B5D' }} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-ink">{d.item_title || 'Certificate'}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="font-mono">{d.code}</span>
                              {d.brand_name && <><span>-</span><span>{d.brand_name}</span></>}
                              {d.legacy && <Badge variant="outline" className="text-[9px] text-muted-foreground border-stone/60" data-testid="legacy-cert-badge">Legacy</Badge>}
                              {d.determination && <span className="text-[10px]" style={{ color: d.determination === 'Authentic' ? '#0E6B5D' : '#5A1F2A' }}>{d.determination}</span>}
                              {d.is_revoked && <Badge variant="outline" className="text-[9px] text-red-600 border-red-200">Revoked</Badge>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="no-underline"><Button size="sm" variant="outline" className="text-xs gap-1"><Download className="h-3 w-3" /> PDF</Button></a>}
                          <Link to={`/verify?code=${d.code}`}><Button size="sm" variant="outline" className="text-xs gap-1"><ExternalLink className="h-3 w-3" /> Verify</Button></Link>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {appraisals.length > 0 && (
            <div>
              <h2 className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-3">Appraisal Reports</h2>
              <div className="space-y-2">
                {appraisals.map((d, i) => (
                  <Card key={i} className="bg-white border-stone/50">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(163,106,58,0.10)' }}><FileText className="h-4 w-4" style={{ color: '#A36A3A' }} /></div>
                        <div>
                          <p className="text-sm font-medium text-ink">{d.item_title || 'Appraisal Report'}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {d.value_cents && <span className="font-semibold text-ink">${(d.value_cents / 100).toLocaleString()}</span>}
                            {d.brand_name && <span>{d.brand_name}</span>}
                          </div>
                        </div>
                      </div>
                      {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="no-underline"><Button size="sm" variant="outline" className="text-xs gap-1"><Download className="h-3 w-3" /> PDF</Button></a>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {letters.length > 0 && (
            <div>
              <h2 className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-3">Other Documents</h2>
              <div className="space-y-2">
                {letters.map((d, i) => (
                  <Card key={i} className="bg-white border-stone/50">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3"><AlertTriangle className="h-4 w-4" style={{ color: '#5A1F2A' }} /><div><p className="text-sm font-medium text-ink">{d.item_title || 'Document'}</p><span className="text-xs text-muted-foreground">Authentication results</span></div></div>
                      {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="no-underline"><Button size="sm" variant="outline" className="text-xs gap-1"><Download className="h-3 w-3" /> PDF</Button></a>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
